import { Copy, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { TimeSelect } from '@/components/shared/time-select';
import { hhmm } from '../catalog/lib';

export interface Interval {
  opensAt: string;
  closesAt: string;
}
/** índice 0 = domingo ... 6 = sábado (igual ao contrato) */
export type Week = Interval[][];

export interface HourRow {
  weekday: number;
  opensAt: string;
  closesAt: string;
}

/** Ordem de exibição: segunda a domingo. */
const ORDER = [1, 2, 3, 4, 5, 6, 0];
const NAMES = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export const emptyWeek = (): Week => Array.from({ length: 7 }, () => []);

export function weekFromRows(rows: HourRow[]): Week {
  const w = emptyWeek();
  for (const r of rows) w[r.weekday]!.push({ opensAt: hhmm(r.opensAt), closesAt: hhmm(r.closesAt) });
  w.forEach((d) => d.sort((a, b) => a.opensAt.localeCompare(b.opensAt)));
  return w;
}

export const weekToIntervals = (w: Week) => w.flatMap((d, weekday) => d.map((i) => ({ weekday, ...i })));

/** Mensagens de erro por dia (índice do dia). */
export function validateWeek(w: Week): Record<number, string> {
  const errs: Record<number, string> = {};
  w.forEach((d, wd) => {
    const sorted = [...d].sort((a, b) => a.opensAt.localeCompare(b.opensAt));
    for (let i = 0; i < sorted.length; i++) {
      const it = sorted[i]!;
      if (!it.opensAt || !it.closesAt) errs[wd] = 'Preencha abertura e fechamento.';
      else if (it.opensAt >= it.closesAt) errs[wd] = 'A abertura deve ser antes do fechamento.';
      else if (i > 0 && it.opensAt < sorted[i - 1]!.closesAt) errs[wd] = 'Os intervalos do dia não podem se sobrepor.';
      if (errs[wd]) break;
    }
  });
  return errs;
}

export function HoursGrid({ value, onChange, errors, disabled }: { value: Week; onChange: (w: Week) => void; errors: Record<number, string>; disabled?: boolean }) {
  const setDay = (wd: number, d: Interval[]) => onChange(value.map((x, i) => (i === wd ? d : x)));
  const copy = (from: number, targets: number[]) => onChange(value.map((x, i) => (targets.includes(i) ? value[from]!.map((v) => ({ ...v })) : x)));

  return (
    <ul className="divide-y rounded-lg border bg-card">
      {ORDER.map((wd) => {
        const day = value[wd]!;
        const open = day.length > 0;
        const others = ORDER.filter((x) => x !== wd);
        return (
          <li key={wd} className="grid gap-2 p-3 sm:grid-cols-[11rem_1fr_auto] sm:items-start">
            <label className="flex items-center gap-3 sm:pt-1.5">
              <Switch
                disabled={disabled}
                checked={open}
                onCheckedChange={(on) => setDay(wd, on ? [{ opensAt: '09:00', closesAt: '18:00' }] : [])}
                aria-label={`${NAMES[wd]}: ${open ? 'aberto' : 'fechado'}`}
              />
              <span className="text-sm font-medium">{NAMES[wd]}</span>
              {!open && <span className="text-xs text-muted-foreground">Fechado</span>}
            </label>
            <div className="grid gap-2">
              {day.map((it, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <TimeSelect
                    disabled={disabled}
                    value={it.opensAt}
                    onChange={(e) => setDay(wd, day.map((x, i) => (i === idx ? { ...x, opensAt: e.target.value } : x)))}
                    aria-label={`${NAMES[wd]}, abertura do intervalo ${idx + 1}`}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">às</span>
                  <TimeSelect
                    disabled={disabled}
                    value={it.closesAt}
                    onChange={(e) => setDay(wd, day.map((x, i) => (i === idx ? { ...x, closesAt: e.target.value } : x)))}
                    aria-label={`${NAMES[wd]}, fechamento do intervalo ${idx + 1}`}
                    className="w-24"
                  />
                  <Button type="button" variant="ghost" size="icon" disabled={disabled} onClick={() => setDay(wd, day.filter((_, i) => i !== idx))} aria-label={`Remover intervalo ${idx + 1} de ${NAMES[wd]}`}>
                    <X aria-hidden />
                  </Button>
                </div>
              ))}
              {open && (
                <div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    onClick={() => {
                      const last = day[day.length - 1]!;
                      const start = last.closesAt < '20:00' ? last.closesAt : '19:00';
                      setDay(wd, [...day, { opensAt: start, closesAt: start < '23:00' ? `${String(Number(start.slice(0, 2)) + 1).padStart(2, '0')}:${start.slice(3)}` : '23:59' }]);
                    }}
                  >
                    <Plus aria-hidden /> Adicionar intervalo
                  </Button>
                  <p className="mt-0.5 text-xs text-muted-foreground">Use dois intervalos para a pausa de almoço.</p>
                </div>
              )}
              {errors[wd] && (
                <p role="alert" className="text-xs text-destructive">
                  {errors[wd]}
                </p>
              )}
            </div>
            {open && !disabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="sm">
                    <Copy aria-hidden /> Copiar para
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Copiar horário de {NAMES[wd]}</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => copy(wd, others)}>Todos os outros dias</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => copy(wd, [1, 2, 3, 4, 5].filter((x) => x !== wd))}>Segunda a sexta</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {others.map((t) => (
                    <DropdownMenuItem key={t} onSelect={() => copy(wd, [t])}>
                      {NAMES[t]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </li>
        );
      })}
    </ul>
  );
}
